import React, { useEffect, useState } from "react";
import { ToastAction, ToastType } from "../../types/toast";

/** Below this, a countdown is noise — the toast is gone before you read it. */
const COUNTDOWN_THRESHOLD_MS = 6000;

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  action?: ToastAction;
  durationMs?: number;
}

export const Toast: React.FC<ToastProps> = ({ message, type, onClose, action, durationMs }) => {
  const showCountdown = !!durationMs && durationMs >= COUNTDOWN_THRESHOLD_MS;
  const [secondsLeft, setSecondsLeft] = useState(() =>
    durationMs ? Math.ceil(durationMs / 1000) : 0
  );

  useEffect(() => {
    if (!showCountdown || !durationMs) return;
    // Derived from a start timestamp rather than decremented: a background tab
    // throttles timers, and a counter that drifts out of step with the actual
    // dismissal is worse than none.
    const startedAt = Date.now();
    const tick = () => {
      const left = Math.ceil((durationMs - (Date.now() - startedAt)) / 1000);
      setSecondsLeft(Math.max(0, left));
    };
    const interval = setInterval(tick, 250);
    tick();
    return () => clearInterval(interval);
  }, [showCountdown, durationMs]);

  const getColors = () => {
    switch (type) {
      case "success":
        return {
          bg: "bg-brand-primary",
          border: "border-brand-primary-light/30",
          icon: "text-white",
          text: "text-white",
          iconName: "check_circle",
          close: "text-white/60 hover:text-white hover:bg-white/10",
          // Filled, not a ghost outline: the action inherited the page's text
          // colour and was invisible against the dark success background.
          action: "bg-white text-brand-primary hover:bg-white/90",
        };
      case "error":
        return {
          bg: "bg-theme-surface",
          border: "border-theme-danger/30",
          icon: "text-theme-danger",
          text: "text-theme-fg-muted",
          iconName: "error",
          close: "text-theme-fg-muted/40 hover:bg-theme-fg-muted/10 hover:text-theme-fg-muted",
          action: "bg-theme-danger text-white hover:opacity-90",
        };
      case "warning":
        return {
          bg: "bg-theme-surface",
          border: "border-amber-500/30",
          icon: "text-amber-500",
          text: "text-theme-fg-muted",
          iconName: "warning",
          close: "text-theme-fg-muted/40 hover:bg-theme-fg-muted/10 hover:text-theme-fg-muted",
          action: "bg-amber-500 text-white hover:opacity-90",
        };
      case "info":
      default:
        return {
          bg: "bg-theme-surface",
          border: "border-blue-500/30",
          icon: "text-blue-500",
          text: "text-theme-fg-muted",
          iconName: "info",
          close: "text-theme-fg-muted/40 hover:bg-theme-fg-muted/10 hover:text-theme-fg-muted",
          action: "bg-blue-500 text-white hover:opacity-90",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      className={`flex items-center gap-3 rounded-full border ${colors.bg} ${colors.border} px-4 py-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] transition-all animate-fadeIn`}
      role="alert"
    >
      <span className={`material-icons text-xl ${colors.icon}`}>
        {colors.iconName}
      </span>
      <span className={`text-[14px] font-semibold ${colors.text} whitespace-nowrap`}>
        {message}
      </span>
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); onClose(); }}
          className={`ml-1 shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold shadow-sm transition-colors ${colors.action}`}
        >
          {action.label}
        </button>
      )}
      {showCountdown && (
        <span
          className={`relative ml-1 flex h-7 w-7 shrink-0 items-center justify-center ${colors.text}`}
          title={`Dismisses in ${secondsLeft}s`}
          aria-hidden="true"
        >
          <svg className="absolute inset-0 h-7 w-7 -rotate-90" viewBox="0 0 28 28">
            <circle
              cx="14" cy="14" r="12" fill="none" strokeWidth="2"
              className="stroke-current opacity-20"
            />
            <circle
              cx="14" cy="14" r="12" fill="none" strokeWidth="2"
              strokeLinecap="round"
              className="stroke-current opacity-70 transition-[stroke-dashoffset] duration-200 ease-linear"
              strokeDasharray={2 * Math.PI * 12}
              strokeDashoffset={
                2 * Math.PI * 12 * (1 - secondsLeft / Math.ceil((durationMs || 1) / 1000))
              }
            />
          </svg>
          <span className="text-[10px] font-semibold tabular-nums">{secondsLeft}</span>
        </span>
      )}
      <button
        onClick={onClose}
        className={`ml-1 flex h-6 w-6 items-center justify-center rounded-full transition-colors ${colors.close}`}
        aria-label="Close"
      >
        <span className="material-icons text-sm">close</span>
      </button>
    </div>
  );
};


